import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Search, 
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  User,
  Briefcase,
  Users,
  Mail,
  Copy,
  Send,
  Camera
} from "lucide-react";

export default function AdminPendingApproval() {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewingApplication, setViewingApplication] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [showInviteDialog, setShowInviteDialog] = useState(null);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-approval`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (appId, status) => {
    try {
      await axios.put(
        `${API}/admin/applications/${appId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Application ${status.toLowerCase()}`);
      setViewingApplication(null);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleSendInvite = async (applicationId, email) => {
    setSendingInvite(true);
    try {
      const response = await axios.post(
        `${API}/admin/registration-invite/${applicationId}`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.email_sent ? "Invitation email sent!" : "Invitation created (email failed to send)");
      setShowInviteDialog(null);
      setInviteEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCopyRegistrationLink = async (applicationId) => {
    try {
      const response = await axios.get(
        `${API}/admin/registration-link/${applicationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigator.clipboard.writeText(response.data.register_url);
      toast.success("Registration link copied to clipboard!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to get registration link");
    }
  };

  const filteredApplications = applications.filter(app => 
    app.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.national_id?.includes(searchTerm)
  );

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-pending-approval-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-primary">Pending Approval</h1>
              <p className="text-text-muted mt-1">Review and approve tenant applications</p>
            </div>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                data-testid="search-applications-input"
                placeholder="Search by name or NIN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 bg-white border-stone-200"
              />
            </div>
          </div>

          {/* Applications List */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg">Pending Applications ({filteredApplications.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-pulse text-primary">Loading...</div>
                </div>
              ) : filteredApplications.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-text-muted">No pending applications</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredApplications.map((app) => (
                    <div 
                      key={app.id} 
                      className="bg-stone-50 rounded-xl p-4 border border-stone-200"
                      data-testid={`pending-app-${app.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                            <Clock className="w-6 h-6 text-yellow-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-primary">{app.full_name}</h3>
                            <div className="flex items-center gap-4 text-sm text-text-muted mt-1">
                              <span>Age: {app.age}</span>
                              <span>•</span>
                              <span>{app.occupation}</span>
                              <span>•</span>
                              <span>NIN: {app.national_id}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setViewingApplication(app)}
                            data-testid={`view-app-btn-${app.id}`}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleStatusUpdate(app.id, "Approved")}
                            data-testid={`approve-btn-${app.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleStatusUpdate(app.id, "Denied")}
                            data-testid={`deny-btn-${app.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Deny
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3 text-sm text-text-muted">
                        Submitted: {new Date(app.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Application Details Dialog */}
      <Dialog open={!!viewingApplication} onOpenChange={() => setViewingApplication(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
            <DialogDescription>Review the complete application</DialogDescription>
          </DialogHeader>
          
          {viewingApplication && (
            <div className="space-y-6 py-4">
              {/* Applicant Photo */}
              {viewingApplication.passport_photo && (
                <div className="flex justify-center">
                  <div className="text-center">
                    <div className="relative inline-block">
                      <img 
                        src={`${API}${viewingApplication.passport_photo}`}
                        alt={`${viewingApplication.full_name}'s photo`}
                        className="w-32 h-32 object-cover rounded-xl border-4 border-primary/20 shadow-lg"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-32 h-32 rounded-xl border-4 border-stone-200 bg-stone-100 items-center justify-center hidden">
                        <Camera className="w-8 h-8 text-stone-400" />
                      </div>
                    </div>
                    <p className="text-sm text-text-muted mt-2">Applicant Photo</p>
                  </div>
                </div>
              )}
              
              {/* Personal Info */}
              <div className="bg-stone-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-primary">Personal Information</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-muted">Full Name:</span>
                    <p className="font-medium">{viewingApplication.full_name}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Age:</span>
                    <p className="font-medium">{viewingApplication.age}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Gender:</span>
                    <p className="font-medium">{viewingApplication.gender || "N/A"}</p>
                  </div>
                  {viewingApplication.mobile_phone && (
                    <div>
                      <span className="text-text-muted">Mobile Phone:</span>
                      <p className="font-medium">{viewingApplication.mobile_phone}</p>
                    </div>
                  )}
                  {viewingApplication.whatsapp_phone && (
                    <div>
                      <span className="text-text-muted">WhatsApp:</span>
                      <p className="font-medium text-green-600">{viewingApplication.whatsapp_phone}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-text-muted">Marital Status:</span>
                    <p className="font-medium">{viewingApplication.marital_status} {viewingApplication.marital_status_other && `(${viewingApplication.marital_status_other})`}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">National ID:</span>
                    <p className="font-medium">{viewingApplication.national_id}</p>
                  </div>
                  {viewingApplication.driver_license && (
                    <div>
                      <span className="text-text-muted">Driver License:</span>
                      <p className="font-medium">{viewingApplication.driver_license}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Previous Addresses */}
              {(viewingApplication.last_address_1 || viewingApplication.last_tenant_address) && (
                <div className="bg-stone-50 rounded-xl p-4">
                  <h3 className="font-semibold text-primary mb-4">Previous Addresses</h3>
                  <div className="space-y-3 text-sm">
                    {viewingApplication.last_address_1 && (
                      <div>
                        <span className="text-text-muted">Last Address 1:</span>
                        <p className="font-medium">{viewingApplication.last_address_1}</p>
                      </div>
                    )}
                    {viewingApplication.last_address_2 && (
                      <div>
                        <span className="text-text-muted">Last Address 2:</span>
                        <p className="font-medium">{viewingApplication.last_address_2}</p>
                      </div>
                    )}
                    {viewingApplication.last_tenant_address && (
                      <div>
                        <span className="text-text-muted">Last Tenant Address:</span>
                        <p className="font-medium">{viewingApplication.last_tenant_address}</p>
                      </div>
                    )}
                    {viewingApplication.duration_at_last_location && (
                      <div>
                        <span className="text-text-muted">Duration of Stay:</span>
                        <p className="font-medium">{viewingApplication.duration_at_last_location}</p>
                      </div>
                    )}
                    {viewingApplication.reason_for_leaving && (
                      <div>
                        <span className="text-text-muted">Reason for Leaving:</span>
                        <p className="font-medium">{viewingApplication.reason_for_leaving}</p>
                      </div>
                    )}
                    {viewingApplication.past_landlord_phone && (
                      <div>
                        <span className="text-text-muted">Past Landlord Phone:</span>
                        <p className="font-medium">{viewingApplication.past_landlord_phone} (Can contact: {viewingApplication.can_landlord_be_contacted})</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Employment */}
              <div className="bg-stone-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Briefcase className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-primary">Employment Details</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-muted">Occupation:</span>
                    <p className="font-medium">{viewingApplication.occupation} {viewingApplication.occupation_other && `(${viewingApplication.occupation_other})`}</p>
                  </div>
                  {viewingApplication.employment_duration && (
                    <div>
                      <span className="text-text-muted">Employment Duration:</span>
                      <p className="font-medium">{viewingApplication.employment_duration}</p>
                    </div>
                  )}
                  {viewingApplication.monthly_salary && (
                    <div>
                      <span className="text-text-muted">Monthly Salary:</span>
                      <p className="font-medium">₦{viewingApplication.monthly_salary}</p>
                    </div>
                  )}
                  {viewingApplication.self_employed_returns && (
                    <div>
                      <span className="text-text-muted">Monthly Returns:</span>
                      <p className="font-medium">₦{viewingApplication.self_employed_returns}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-text-muted">Additional Occupants:</span>
                    <p className="font-medium">{viewingApplication.additional_occupants}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Planned Rental Duration:</span>
                    <p className="font-medium">{viewingApplication.rental_duration}</p>
                  </div>
                </div>
              </div>

              {/* Guarantors */}
              <div className="bg-stone-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-primary">Guarantors</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-medium mb-2">Guarantor 1</h4>
                    <p><span className="text-text-muted">Name:</span> {viewingApplication.guarantor_1?.name}</p>
                    <p><span className="text-text-muted">Phone:</span> {viewingApplication.guarantor_1?.phone}</p>
                    <p><span className="text-text-muted">Address:</span> {viewingApplication.guarantor_1?.address}</p>
                    <p><span className="text-text-muted">Can be contacted:</span> {viewingApplication.guarantor_1?.can_be_contacted}</p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Guarantor 2</h4>
                    <p><span className="text-text-muted">Name:</span> {viewingApplication.guarantor_2?.name}</p>
                    <p><span className="text-text-muted">Phone:</span> {viewingApplication.guarantor_2?.phone}</p>
                    <p><span className="text-text-muted">Address:</span> {viewingApplication.guarantor_2?.address}</p>
                    <p><span className="text-text-muted">Can be contacted:</span> {viewingApplication.guarantor_2?.can_be_contacted}</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-stone-200">
                <Button
                  variant="outline"
                  onClick={() => setViewingApplication(null)}
                >
                  Close
                </Button>
                
                {viewingApplication.status === "Approved" ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handleCopyRegistrationLink(viewingApplication.id)}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      data-testid="copy-register-link-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Registration Link
                    </Button>
                    <Button
                      className="bg-primary hover:bg-primary-800"
                      onClick={() => {
                        setInviteEmail(viewingApplication.applicant_email || "");
                        setShowInviteDialog(viewingApplication);
                      }}
                      data-testid="send-invite-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send Registration Invite
                    </Button>
                  </>
                ) : viewingApplication.status === "Pending Approval" ? (
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => handleStatusUpdate(viewingApplication.id, "Denied")}
                      data-testid="modal-deny-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Deny Application
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleStatusUpdate(viewingApplication.id, "Approved")}
                      data-testid="modal-approve-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve Application
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Invite Dialog */}
      <Dialog open={!!showInviteDialog} onOpenChange={() => setShowInviteDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Registration Invite</DialogTitle>
            <DialogDescription>
              Send an email invitation to {showInviteDialog?.full_name} to create their account
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Email Address</label>
              <Input
                type="email"
                placeholder="Enter email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email-input"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowInviteDialog(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => handleSendInvite(showInviteDialog.id, inviteEmail)}
                disabled={!inviteEmail || sendingInvite}
                className="bg-primary hover:bg-primary-800"
              >
                {sendingInvite ? "Sending..." : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Invite
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
